import { jsPDF } from 'jspdf'
import type { Ref, ComputedRef } from 'vue'
import type { Submission } from '@/store/submissions'

export function useReportExport(options: {
  selectedDocs: Ref<string[]>
  documentsItems: ComputedRef<Submission[]> | Ref<Submission[]>
  formatDateOnly: (v: string) => string
  siteName?: Ref<string> | ComputedRef<string>
  systemColor?: Ref<string> | ComputedRef<string>
  logoText?: Ref<string> | ComputedRef<string>
}) {
  const { selectedDocs, documentsItems, formatDateOnly, siteName, systemColor, logoText } = options
  const DEFAULT_getBranding = () => 'ObraTycoon by GusMachado'
  const getBranding = () => logoText?.value || DEFAULT_getBranding()
  const getSiteName = () => (siteName?.value || '')

  const hexToRgb = (hex: string): [number, number, number] => {
    const clean = hex.replace('#', '')
    const r = parseInt(clean.substring(0, 2), 16)
    const g = parseInt(clean.substring(2, 4), 16)
    const b = parseInt(clean.substring(4, 6), 16)
    return [isNaN(r) ? 30 : r, isNaN(g) ? 64 : g, isNaN(b) ? 175 : b]
  }

  const getAccentHex = (): string => {
    const hex = systemColor?.value || '#1E40AF'
    return hex.replace('#', '').toUpperCase()
  }

  const getAccentRgb = (): [number, number, number] => hexToRgb(systemColor?.value || '#1E40AF')

  const getAccentLightHex = (): string => {
    const [r, g, b] = getAccentRgb()
    return [r, g, b]
      .map(v => Math.min(255, Math.round(v + (255 - v) * 0.87)).toString(16).padStart(2, '0'))
      .join('').toUpperCase()
  }

  const getAccentLightRgb = (): [number, number, number] => {
    const [r, g, b] = getAccentRgb()
    return [
      Math.min(255, Math.round(r + (255 - r) * 0.87)),
      Math.min(255, Math.round(g + (255 - g) * 0.87)),
      Math.min(255, Math.round(b + (255 - b) * 0.87)),
    ]
  }

  const getAccentBandTextRgb = (): [number, number, number] => {
    const [r, g, b] = getAccentRgb()
    return [
      Math.min(255, Math.round(r + (255 - r) * 0.58)),
      Math.min(255, Math.round(g + (255 - g) * 0.58)),
      Math.min(255, Math.round(b + (255 - b) * 0.58)),
    ]
  }

  const getAccentBrandTextRgb = (): [number, number, number] => {
    const [r, g, b] = getAccentRgb()
    return [
      Math.min(255, Math.round(r + (255 - r) * 0.70)),
      Math.min(255, Math.round(g + (255 - g) * 0.70)),
      Math.min(255, Math.round(b + (255 - b) * 0.70)),
    ]
  }

  const sanitizeFilename = (value: string) =>
    value
      .replace(/[^\w-]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 60)
      .toLowerCase() || 'documento'

  // ── Translation via MyMemory (free, no API key) ───────────────────────
  const MYMEMORY_CODES: Record<string, string> = {
    pt: 'pt-BR', en: 'en-US', es: 'es-ES', de: 'de-DE', fr: 'fr-FR',
    it: 'it-IT', zh: 'zh-CN', ja: 'ja-JP', ko: 'ko-KR', ru: 'ru-RU', nl: 'nl-NL',
  }
  const translateText = async (text: string, from: string, to: string): Promise<string> => {
    if (!text.trim() || from === to) return text
    const lp = `${MYMEMORY_CODES[from] ?? from}|${MYMEMORY_CODES[to] ?? to}`
    try {
      const res = await fetch(`https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=${encodeURIComponent(lp)}`)
      const data = await res.json()
      if (data.responseStatus === 200 && data.responseData?.translatedText) {
        return data.responseData.translatedText as string
      }
    } catch { /* fall through */ }
    return text
  }

  const translateList = async (items: string[], from: string, to: string) => {
    if (from === to) return items
    // batch: join with a unique separator, translate once, then split
    const SEP = '\u2063|\u2063'
    const joined = items.join(SEP)
    if (!joined.trim()) return items
    const translated = await translateText(joined, from, to)
    const parts = translated.split(SEP)
    // fall back to per-item if separator got mangled
    if (parts.length === items.length) return parts.map(s => s.trim())
    return Promise.all(items.map(t => translateText(t, from, to)))
  }

  // ── Image helpers ─────────────────────────────────────────────────────
  const imgToBase64 = (src: string, apiBase: string): Promise<string> =>
    new Promise((resolve) => {
      if (src.startsWith('data:')) { resolve(src); return }
      const url = src.startsWith('/uploads/') ? `${apiBase}${src}` : src
      const img = new Image()
      img.crossOrigin = 'anonymous'
      img.onload = () => {
        const canvas = document.createElement('canvas')
        canvas.width = img.naturalWidth; canvas.height = img.naturalHeight
        const ctx = canvas.getContext('2d')
        if (!ctx) { resolve(''); return }
        ctx.drawImage(img, 0, 0)
        resolve(canvas.toDataURL('image/jpeg', 0.85))
      }
      img.onerror = () => resolve('')
      img.src = url
    })

  const getImgDimensions = (src: string): Promise<{ w: number; h: number }> =>
    new Promise((resolve) => {
      const img = new Image()
      img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight })
      img.onerror = () => resolve({ w: 0, h: 0 })
      img.src = src
    })

  const REPORT_LABELS_MAP: Record<string, {
    headingDaily: string; headingIncident: string; headingGeneral: string;
    project: string; date: string; supervisor: string; collaborators: string;
    tools: string; activities: string; observations: string
  }> = {
    pt: { headingDaily: 'Relatório Diário de Obra', headingIncident: 'Relatório de Incidente', headingGeneral: 'Relatório Geral', project: 'Projeto', date: 'Data', supervisor: 'Supervisor', collaborators: 'Colaboradores', tools: 'Ferramentas', activities: 'Atividades executadas', observations: 'Observações' },
    en: { headingDaily: 'Daily Construction Report', headingIncident: 'Incident Report', headingGeneral: 'General Report', project: 'Project', date: 'Date', supervisor: 'Supervisor', collaborators: 'Collaborators', tools: 'Tools', activities: 'Performed activities', observations: 'Observations' },
    es: { headingDaily: 'Informe Diario de Obra', headingIncident: 'Informe de Incidente', headingGeneral: 'Informe General', project: 'Proyecto', date: 'Fecha', supervisor: 'Supervisor', collaborators: 'Colaboradores', tools: 'Herramientas', activities: 'Actividades realizadas', observations: 'Observaciones' },
    de: { headingDaily: 'Täglicher Baubericht', headingIncident: 'Vorfallbericht', headingGeneral: 'Allgemeiner Bericht', project: 'Projekt', date: 'Datum', supervisor: 'Supervisor', collaborators: 'Mitarbeiter', tools: 'Werkzeuge', activities: 'Durchgeführte Aktivitäten', observations: 'Beobachtungen' },
    fr: { headingDaily: 'Rapport Journalier de Chantier', headingIncident: 'Rapport d\'Incident', headingGeneral: 'Rapport Général', project: 'Projet', date: 'Date', supervisor: 'Superviseur', collaborators: 'Collaborateurs', tools: 'Outils', activities: 'Activités réalisées', observations: 'Observations' },
    it: { headingDaily: 'Rapporto Giornaliero di Cantiere', headingIncident: 'Rapporto di Incidente', headingGeneral: 'Rapporto Generale', project: 'Progetto', date: 'Data', supervisor: 'Supervisore', collaborators: 'Collaboratori', tools: 'Strumenti', activities: 'Attività svolte', observations: 'Osservazioni' },
    zh: { headingDaily: '每日施工报告', headingIncident: '事故报告', headingGeneral: '综合报告', project: '项目', date: '日期', supervisor: '监督员', collaborators: '协作人员', tools: '工具', activities: '已执行活动', observations: '备注' },
    ja: { headingDaily: '日次建設報告', headingIncident: '事故報告書', headingGeneral: '総合報告書', project: 'プロジェクト', date: '日付', supervisor: '監督者', collaborators: '協力者', tools: 'ツール', activities: '実施した活動', observations: '所見' },
    ko: { headingDaily: '일일 건설 보고서', headingIncident: '사고 보고서', headingGeneral: '일반 보고서', project: '프로젝트', date: '날짜', supervisor: '감독자', collaborators: '협력자', tools: '도구', activities: '수행된 활동', observations: '관찰 사항' },
    ru: { headingDaily: 'Ежедневный отчёт о строительстве', headingIncident: 'Отчёт об инциденте', headingGeneral: 'Общий отчёт', project: 'Проект', date: 'Дата', supervisor: 'Супервайзер', collaborators: 'Сотрудники', tools: 'Инструменты', activities: 'Выполненные работы', observations: 'Замечания' },
    nl: { headingDaily: 'Dagelijks bouwrapport', headingIncident: 'Incidentrapport', headingGeneral: 'Algemeen rapport', project: 'Project', date: 'Datum', supervisor: 'Supervisor', collaborators: 'Medewerkers', tools: 'Gereedschap', activities: 'Uitgevoerde activiteiten', observations: 'Opmerkingen' },
  }

  const KNOWN_TITLES = new Set(['Relatorio diario de obra', 'Relatorio de incidente', 'Relatorio geral'])

  const reportLabels = (item: Submission, lang?: string) => {
    const code = lang || item.reportLanguage?.toLowerCase() || 'pt'
    const map = REPORT_LABELS_MAP[code] || REPORT_LABELS_MAP['pt']
    let heading: string
    if (item.title === 'Relatorio diario de obra') heading = map.headingDaily
    else if (item.title === 'Relatorio de incidente') heading = map.headingIncident
    else if (item.title === 'Relatorio geral') heading = map.headingGeneral
    else heading = KNOWN_TITLES.has(item.title) ? map.headingDaily : (item.title || map.headingDaily)
    return {
      heading,
      project: map.project,
      date: map.date,
      supervisor: map.supervisor,
      collaborators: map.collaborators,
      tools: map.tools,
      activities: map.activities,
      observations: map.observations,
    }
  }

  const downloadBlob = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.click()
    URL.revokeObjectURL(url)
  }

  // ── Word (.docx) export ───────────────────────────────────────────────
  const exportSelectedDocsAsDoc = async (lang?: string) => {
    const docs = documentsItems.value.filter((item) => selectedDocs.value.includes(item.id))
    if (!docs.length) return

    const apiBase = process.env.VUE_APP_API_BASE || 'http://localhost:3001'
    const useZip = docs.length > 1
    const JSZipModule = useZip ? await import('jszip') : null
    const zip = useZip && JSZipModule ? new (JSZipModule.default || JSZipModule as any)() : null

    const {
      Document, Paragraph, TextRun, Table, TableRow, TableCell,
      HeadingLevel, AlignmentType, Packer, WidthType, ImageRun,
      BorderStyle, convertInchesToTwip, ShadingType,
      Header, Footer, PageNumber,
    } = await import('docx')

    for (const item of docs) {
      const srcLang = item.reportLanguage || 'pt'
      const tgtLang = lang || srcLang
      const labels = reportLabels(item, tgtLang)
      const date = formatDateOnly(item.reportDate)
      const projectName = item.attraction || 'projeto'

      // translate content
      const rawTools   = (item.tools || []).filter(t => t.text || t.images?.length)
      const rawObs     = (item.observations || []).filter(o => o.text || o.images?.length)
      const rawCollabs = (item.collaborators || []).filter(Boolean)
      const rawActs    = (item.activities || [])

      const [trToolTexts, trObsTexts, ...trActDescs] = await Promise.all([
        translateList(rawTools.map(t => t.text), srcLang, tgtLang),
        translateList(rawObs.map(o => o.text), srcLang, tgtLang),
        ...rawActs.map(a => translateText(a.description || '', srcLang, tgtLang)),
      ])

      const ACCENT = getAccentHex()
      const LIGHT  = getAccentLightHex()

      const makeHeading = (text: string) => new Paragraph({
        children: [new TextRun({ text, bold: true, size: 26, color: ACCENT })],
        spacing: { before: 300, after: 100 },
        border: { bottom: { style: BorderStyle.SINGLE, size: 4, color: ACCENT, space: 4 } },
      })

      const makeBullet = (text: string) => new Paragraph({
        children: [new TextRun({ text: text || '-', size: 22 })],
        bullet: { level: 0 },
        spacing: { after: 60 },
      })

      const metaTable = new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        rows: [
          [labels.project, projectName],
          [labels.date, date],
          [labels.supervisor, item.author.name || '-'],
        ].map(([label, value]) => new TableRow({
          children: [
            new TableCell({
              width: { size: 25, type: WidthType.PERCENTAGE },
              shading: { type: ShadingType.CLEAR, fill: LIGHT },
              children: [new Paragraph({ children: [new TextRun({ text: label, bold: true, size: 20, color: ACCENT })] })],
            }),
            new TableCell({
              width: { size: 75, type: WidthType.PERCENTAGE },
              children: [new Paragraph({ children: [new TextRun({ text: value, size: 20 })] })],
            }),
          ],
        })),
      })

      // helper: build section children (text bullet + images) for tools/obs
      const buildSectionChildren = async (sections: typeof rawTools, translatedTexts: string[]) => {
        const children: any[] = []
        for (let i = 0; i < sections.length; i++) {
          const text = translatedTexts[i] || sections[i].text || '-'
          children.push(makeBullet(text))
          for (const imgSrc of (sections[i].images || [])) {
            const b64 = await imgToBase64(imgSrc, apiBase)
            if (!b64 || !b64.startsWith('data:image/')) continue
            const { w, h } = await getImgDimensions(b64)
            if (!w || !h) continue
            const MAX_PX_W = Math.round(5.5 * 96)
            const ratio = h / w
            const drawWpx = Math.min(w, MAX_PX_W)
            const drawHpx = Math.round(drawWpx * ratio)
            const base64Data = b64.split(',')[1]
            const binary = atob(base64Data)
            const bytes = new Uint8Array(binary.length)
            for (let j = 0; j < binary.length; j++) bytes[j] = binary.charCodeAt(j)
            children.push(new Paragraph({
              children: [new ImageRun({ data: bytes.buffer, transformation: { width: drawWpx, height: drawHpx }, type: 'jpg' })],
              spacing: { after: 80 },
            }))
          }
        }
        return children
      }

      const toolChildren = await buildSectionChildren(rawTools, trToolTexts)
      const obsChildren  = await buildSectionChildren(rawObs, trObsTexts)

      // activity blocks with images
      const activityChildren: any[] = []
      for (let i = 0; i < rawActs.length; i++) {
        const act = rawActs[i]
        const desc = trActDescs[i] || act.description || '-'
        activityChildren.push(new Paragraph({
          children: [
            new TextRun({ text: `${i + 1}.  `, bold: true, size: 22, color: ACCENT }),
            new TextRun({ text: desc, size: 22 }),
          ],
          spacing: { after: 80 },
        }))
        for (const imgSrc of (act.images || [])) {
          const b64 = await imgToBase64(imgSrc, apiBase)
          if (!b64 || !b64.startsWith('data:image/')) continue
          const { w, h } = await getImgDimensions(b64)
          if (!w || !h) continue
          // max width = 5.5 inches, in pixels at 96dpi
          const MAX_PX_W = Math.round(5.5 * 96)
          const ratio = h / w
          const drawWpx = Math.min(w, MAX_PX_W)
          const drawHpx = Math.round(drawWpx * ratio)
          const base64Data = b64.split(',')[1]
          const binary = atob(base64Data)
          const bytes = new Uint8Array(binary.length)
          for (let j = 0; j < binary.length; j++) bytes[j] = binary.charCodeAt(j)
          activityChildren.push(new Paragraph({
            children: [new ImageRun({ data: bytes.buffer, transformation: { width: drawWpx, height: drawHpx }, type: 'jpg' })],
            spacing: { after: 100 },
          }))
        }
      }

      const obraName = getSiteName()
      const wordDoc = new Document({
        styles: {
          default: { document: { run: { font: 'Calibri', size: 22, color: '1f1e1a' } } },
        },
        sections: [{
          properties: { page: { margin: { top: convertInchesToTwip(1), bottom: convertInchesToTwip(1), left: convertInchesToTwip(1.1), right: convertInchesToTwip(1.1) } } },
          headers: {
            default: new Header({
              children: [
                new Paragraph({
                  alignment: AlignmentType.RIGHT,
                  spacing: { after: 0 },
                  children: [
                    ...(obraName ? [new TextRun({ text: obraName, bold: true, size: 22, color: ACCENT }), new TextRun({ text: '\n', break: 1 })] : []),
                    new TextRun({ text: getBranding(), size: 16, color: '888880' }),
                  ],
                }),
              ],
            }),
          },
          footers: {
            default: new Footer({
              children: [
                new Paragraph({
                  alignment: AlignmentType.LEFT,
                  children: [
                    new TextRun({ text: getBranding(), size: 16, color: '888880' }),
                    new TextRun({ text: '    ' }),
                    new TextRun({ children: [PageNumber.CURRENT], size: 16, color: '888880' }),
                    new TextRun({ text: ' / ', size: 16, color: '888880' }),
                    new TextRun({ children: [PageNumber.TOTAL_PAGES], size: 16, color: '888880' }),
                  ],
                }),
              ],
            }),
          },
          children: [
            // Title block
            new Paragraph({
              children: [new TextRun({ text: labels.heading, bold: true, size: 40, color: ACCENT })],
              alignment: AlignmentType.LEFT,
              spacing: { after: 200 },
              border: { bottom: { style: BorderStyle.SINGLE, size: 8, color: ACCENT, space: 6 } },
            }),
            new Paragraph({ spacing: { after: 100 }, children: [] }),
            metaTable,
            new Paragraph({ spacing: { after: 200 }, children: [] }),
            ...(rawCollabs.length ? [
              makeHeading(labels.collaborators),
              ...rawCollabs.map(makeBullet),
              new Paragraph({ spacing: { after: 100 }, children: [] }),
            ] : []),
            makeHeading(labels.tools),
            ...(toolChildren.length ? toolChildren : [makeBullet('-')]),
            new Paragraph({ spacing: { after: 100 }, children: [] }),
            makeHeading(labels.activities),
            ...(activityChildren.length ? activityChildren : [makeBullet('-')]),
            new Paragraph({ spacing: { after: 100 }, children: [] }),
            makeHeading(labels.observations),
            ...(obsChildren.length ? obsChildren : [makeBullet('-')]),
          ],
        }],
      })

      const filename = `${sanitizeFilename(labels.heading)}-${sanitizeFilename(projectName)}-${date.replace(/\//g, '-')}.docx`
      const blob = await Packer.toBlob(wordDoc)
      if (zip) {
        zip.file(filename, blob)
      } else {
        downloadBlob(blob, filename)
      }
    }

    if (zip) {
      const zipBlob = await zip.generateAsync({ type: 'blob' })
      downloadBlob(zipBlob, `relatorios-${new Date().toISOString().slice(0, 10)}.zip`)
    }

    selectedDocs.value = []
  }

  // ── PDF export ────────────────────────────────────────────────────────
  const exportSelectedDocsAsPdf = async (lang?: string, imageSizeFactors?: Record<string, number>, imgPaired?: Record<string, boolean>) => {
    const docs = documentsItems.value.filter((item) => selectedDocs.value.includes(item.id))
    if (!docs.length) return

    const apiBase = process.env.VUE_APP_API_BASE || 'http://localhost:3001'
    const getImageType = (dataUrl: string) => (dataUrl.startsWith('data:image/png') ? 'PNG' : 'JPEG')
    const useZip = docs.length > 1
    const JSZipModule = useZip ? await import('jszip') : null
    const zip = useZip && JSZipModule ? new (JSZipModule.default || JSZipModule as any)() : null

    for (const item of docs) {
      const srcLang = item.reportLanguage || 'pt'
      const tgtLang = lang || srcLang
      const labels  = reportLabels(item, tgtLang)
      const heading = labels.heading
      const projectName = item.attraction || 'projeto'
      const date = formatDateOnly(item.reportDate)

      // translate content
      const rawTools   = (item.tools || []).filter(t => t.text || t.images?.length)
      const rawObs     = (item.observations || []).filter(o => o.text || o.images?.length)
      const rawCollabs = (item.collaborators || []).filter(Boolean)
      const rawActs    = (item.activities || [])

      const [trToolTexts, trObsTexts, ...trActDescs] = await Promise.all([
        translateList(rawTools.map(t => t.text), srcLang, tgtLang),
        translateList(rawObs.map(o => o.text), srcLang, tgtLang),
        ...rawActs.map(a => translateText(a.description || '', srcLang, tgtLang)),
      ])

      const pdf = new jsPDF({ unit: 'pt', format: 'a4' })
      const pageW  = pdf.internal.pageSize.getWidth()
      const pageH  = pdf.internal.pageSize.getHeight()
      const mX     = 56
      const mY     = 56
      const mBot   = 50
      const cW     = pageW - mX * 2
      let y        = mY

      // colour constants (RGB arrays)
      const ACCENT: [number,number,number] = getAccentRgb()
      const LIGHT:  [number,number,number] = getAccentLightRgb()
      const INK:    [number,number,number] = [31, 30, 26]
      const MUTED:  [number,number,number] = [120, 116, 108]

      const addFooter = (pageNum: number) => {
        const total = (pdf as any).internal.getNumberOfPages()
        pdf.setFont('helvetica', 'normal')
        pdf.setFontSize(8)
        pdf.setTextColor(...MUTED)
        pdf.text(getBranding(), mX, pageH - 20)
        pdf.text(`${pageNum} / ${total}`, pageW - mX, pageH - 20, { align: 'right' })
        pdf.setTextColor(...INK)
      }

      const ensureSpace = (need: number) => {
        if (y + need <= pageH - mBot - 20) return
        addFooter((pdf as any).internal.getCurrentPageInfo().pageNumber)
        pdf.addPage()
        y = mY
      }

      const drawSection = (title: string) => {
        ensureSpace(46)
        pdf.setFillColor(...ACCENT)
        pdf.rect(mX, y, cW, 24, 'F')
        pdf.setFont('helvetica', 'bold')
        pdf.setFontSize(11)
        pdf.setTextColor(255, 255, 255)
        pdf.text(title.toUpperCase(), mX + 10, y + 16)
        pdf.setTextColor(...INK)
        y += 36
      }

      const LINE_H = 17
      const drawBullet = (text: string) => {
        pdf.setFont('helvetica', 'normal')
        pdf.setFontSize(10.5)
        pdf.setTextColor(...INK)
        const lines = pdf.splitTextToSize(text || '-', cW - 18) as string[]
        ensureSpace(LINE_H * lines.length + 10)
        pdf.setFillColor(...ACCENT)
        pdf.circle(mX + 5, y - 4, 2.8, 'F')
        pdf.text(lines, mX + 14, y)
        y += LINE_H * lines.length + 8
      }

      const drawImagePdf = async (imgSrc: string, sizeFactor = 1) => {
        const b64 = await imgToBase64(imgSrc, apiBase)
        if (!b64 || !b64.startsWith('data:image/')) return
        const imgType = getImageType(b64)
        try {
          const props = pdf.getImageProperties(b64)
          const iW = Number(props.width || 0)
          const iH = Number(props.height || 0)
          if (!iW || !iH) return
          let dW = cW * sizeFactor, dH = (dW * iH) / iW
          const maxH = pageH - mY - mBot - 20
          if (dH > maxH) { dH = maxH; dW = (dH * iW) / iH }
          ensureSpace(dH + 10)
          pdf.addImage(b64, imgType, mX, y, dW, dH)
          y += dH + 10
        } catch { /* skip */ }
      }

      // Render two images side-by-side in a single PDF row
      const drawImagesPdfPair = async (srcA: string, srcB: string, factorA = 1, factorB = 1) => {
        const [b64A, b64B] = await Promise.all([imgToBase64(srcA, apiBase), imgToBase64(srcB, apiBase)])
        const validA = b64A && b64A.startsWith('data:image/')
        const validB = b64B && b64B.startsWith('data:image/')
        if (!validA && !validB) return
        if (!validA) { await drawImagePdf(srcB, factorB); return }
        if (!validB) { await drawImagePdf(srcA, factorA); return }
        try {
          const pA = pdf.getImageProperties(b64A)
          const pB = pdf.getImageProperties(b64B)
          const gap = 8
          const colW = (cW - gap) / 2
          const dWA = colW, dHA = (dWA * Number(pA.height)) / Number(pA.width)
          const dWB = colW, dHB = (dWB * Number(pB.height)) / Number(pB.width)
          const rowH = Math.max(dHA, dHB)
          ensureSpace(rowH + 10)
          pdf.addImage(b64A, getImageType(b64A), mX, y, dWA, dHA)
          pdf.addImage(b64B, getImageType(b64B), mX + colW + gap, y, dWB, dHB)
          y += rowH + 10
        } catch { /* skip */ }
      }

      // Groups image list into solo or paired chunks using imgPaired map
      const groupImagesForPdf = (images: string[]): Array<[string] | [string, string]> => {
        const chunks: Array<[string] | [string, string]> = []
        let i = 0
        while (i < images.length) {
          if (imgPaired?.[images[i]] && i + 1 < images.length && imgPaired?.[images[i + 1]]) {
            chunks.push([images[i], images[i + 1]])
            i += 2
          } else {
            chunks.push([images[i]])
            i++
          }
        }
        return chunks
      }

      const drawImagesForPdf = async (images: string[]) => {
        for (const chunk of groupImagesForPdf(images)) {
          if (chunk.length === 2) {
            await drawImagesPdfPair(chunk[0], chunk[1], imageSizeFactors?.[chunk[0]] ?? 0.5, imageSizeFactors?.[chunk[1]] ?? 0.5)
          } else {
            await drawImagePdf(chunk[0], imageSizeFactors?.[chunk[0]] ?? 1)
          }
        }
      }

      // ── Header band — calculate height dynamically ───────────────────
      const obraName = getSiteName()
      const LEFT_W  = pageW * 0.52          // left column: 52% of page
      const RIGHT_W = pageW * 0.40          // right column: 40% of page
      const TITLE_LINE_H = 22               // px per title line at font 18
      const META_LINE_H  = 14               // px per meta line at font 10
      const BRAND_LINE_H = 12               // px per brand line at font 8/11

      pdf.setFont('helvetica', 'bold')
      pdf.setFontSize(18)
      const titleLines = pdf.splitTextToSize(heading, LEFT_W - mX) as string[]

      pdf.setFont('helvetica', 'normal')
      pdf.setFontSize(10)
      const metaStr = `${labels.project}: ${projectName}   |   ${labels.date}: ${date}   |   ${labels.supervisor}: ${item.author.name || '-'}`
      const metaLines = pdf.splitTextToSize(metaStr, LEFT_W - mX) as string[]

      pdf.setFont('helvetica', 'bold')
      pdf.setFontSize(11)
      const obraLines = obraName ? pdf.splitTextToSize(obraName, RIGHT_W) as string[] : []

      // Left height: title + gap + meta
      const leftH = titleLines.length * TITLE_LINE_H + 6 + metaLines.length * META_LINE_H
      // Right height: obra lines + gap + branding line
      const rightH = obraLines.length * BRAND_LINE_H + (obraLines.length ? 5 : 0) + BRAND_LINE_H
      const bandH = Math.max(leftH, rightH) + 24  // 12px padding top+bottom

      pdf.setFillColor(...ACCENT)
      pdf.rect(0, 0, pageW, bandH, 'F')

      // Left: title then meta
      const titleY = 16 + TITLE_LINE_H
      pdf.setFont('helvetica', 'bold')
      pdf.setFontSize(18)
      pdf.setTextColor(255, 255, 255)
      pdf.text(titleLines, mX, titleY)

      const metaY = titleY + titleLines.length * TITLE_LINE_H + 6
      pdf.setFont('helvetica', 'normal')
      pdf.setFontSize(10)
      pdf.setTextColor(...getAccentBandTextRgb())
      pdf.text(metaLines, mX, metaY)

      // Right: obra name (wrappable) then branding
      let rightY = 16 + BRAND_LINE_H
      if (obraLines.length) {
        pdf.setFont('helvetica', 'bold')
        pdf.setFontSize(11)
        pdf.setTextColor(255, 255, 255)
        for (const line of obraLines) {
          pdf.text(line, pageW - mX, rightY, { align: 'right' })
          rightY += BRAND_LINE_H + 2
        }
        rightY += 3
      }
      pdf.setFont('helvetica', 'normal')
      pdf.setFontSize(8)
      pdf.setTextColor(...getAccentBrandTextRgb())
      pdf.text(getBranding(), pageW - mX, rightY, { align: 'right' })

      pdf.setTextColor(...INK)
      y = bandH + 30

      // ── Collaborators ─────────────────────────────────────────────────
      if (rawCollabs.length) {
        drawSection(labels.collaborators)
        rawCollabs.forEach(drawBullet)
        y += 20
      }

      // ── Tools ────────────────────────────────────────────────────────
      drawSection(labels.tools)
      if (rawTools.length) {
        for (let i = 0; i < rawTools.length; i++) {
          drawBullet(trToolTexts[i] || rawTools[i].text || '-')
          await drawImagesForPdf(rawTools[i].images || [])
        }
      } else drawBullet('-')
      y += 20

      // ── Activities ───────────────────────────────────────────────────
      drawSection(labels.activities)
      if (rawActs.length) {
        for (let i = 0; i < rawActs.length; i++) {
          const desc = trActDescs[i] || rawActs[i].description || '-'
          ensureSpace(24)
          pdf.setFont('helvetica', 'bold')
          pdf.setFontSize(10.5)
          pdf.setTextColor(...ACCENT)
          pdf.text(`${i + 1}.`, mX, y)
          pdf.setFont('helvetica', 'normal')
          pdf.setTextColor(...INK)
          const lines = pdf.splitTextToSize(desc, cW - 18) as string[]
          pdf.text(lines, mX + 14, y)
          y += LINE_H * lines.length + 10
          await drawImagesForPdf(rawActs[i].images || [])
          y += 8
        }
      } else drawBullet('-')
      y += 20

      // ── Observations ─────────────────────────────────────────────────
      drawSection(labels.observations)
      if (rawObs.length) {
        for (let i = 0; i < rawObs.length; i++) {
          drawBullet(trObsTexts[i] || rawObs[i].text || '-')
          await drawImagesForPdf(rawObs[i].images || [])
        }
      } else drawBullet('-')

      // ── Footer on last page ──────────────────────────────────────────
      addFooter((pdf as any).internal.getCurrentPageInfo().pageNumber)

      const filename = `${sanitizeFilename(heading)}-${sanitizeFilename(projectName)}-${date.replace(/\//g, '-')}.pdf`
      if (zip) {
        const pdfBlob = pdf.output('blob')
        zip.file(filename, pdfBlob)
      } else {
        pdf.save(filename)
      }
    }

    if (zip) {
      const zipBlob = await zip.generateAsync({ type: 'blob' })
      downloadBlob(zipBlob, `relatorios-${new Date().toISOString().slice(0, 10)}.zip`)
    }

    selectedDocs.value = []
  }

  return { exportSelectedDocsAsDoc, exportSelectedDocsAsPdf, reportLabels, getAccentRgb }
}
